// stores/option.js
import { defineStore } from 'pinia'

// 앱 최초 로드 시 /api/options/readOption (options-util-wise 라이브러리 endpoint) 으로 받아온 옵션 정보를 보관한다.
export const useOptionStore = defineStore('option', {
  state: () => ({
    option: {}, // Map 형태의 옵션 정보
    isLoaded: false,
  }),

  getters: {
    // 옵션 단건 조회 (값이 없으면 defaultValue)
    // "mypage.coupon.view" 처럼 점이 들어간 키는 평면 키 -> 중첩 객체 순으로 찾는다
    getOption: (state) => (key, defaultValue = null) => {
      if (state.option?.[key] !== undefined) return state.option[key]

      const value = String(key)
        .split('.')
        .reduce((acc, name) => (acc == null ? acc : acc[name]), state.option)

      return value ?? defaultValue
    },

    // 사용 여부 옵션(Y/N) 판정. 값이 없으면 미사용으로 본다.
    isOn() {
      return (key) => this.getOption(key) === 'Y'
    },
  },

  actions: {
    setOption(option) {
      this.option = option ?? {}
      this.isLoaded = true
    },

    // 앱 최초 로드 시 1회 조회한다 (app.vue)
    // 라이브러리 endpoint 는 resCd 없이 {key: 값} 만 돌려주므로 useApi 를 거치지 않고 직접 호출한다
    // (useApi 는 resCd 가 0000 이 아니면 오류 토스트를 띄운다)
    //
    // 주소에 option 이 붙어 있으면 그대로 서버에 넘긴다. 서버가 세션 덮어쓰기를 켜 둔 환경이면
    // (options.web.session-override.enabled) 그 값을 세션에 저장해 서버 쪽 옵션 판정에도 같은 값이 쓰인다.
    // 세션이 이어지도록 쿠키를 함께 보낸다.
    async fetchOption() {
      const config = useRuntimeConfig()
      try {
        const queryOption = import.meta.client ? new URLSearchParams(window.location.search).get('option') : null
        const query = queryOption !== null ? `?option=${encodeURIComponent(queryOption)}` : ''
        const response = await fetch(`${config.public.apiServer}/api/options/readOption${query}`, {
          credentials: 'include',
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        this.setOption(await response.json())
      } catch (error) {
        console.error('옵션 조회 오류:', error)
      }
      this.applyQueryOption()
    },

    /**
     * 테스트용으로 주소에 option 을 붙이면 그 값으로 덮어쓴다 (새로고침 전까지 유지).
     *
     * 두 가지 형식을 받는다.
     *   ?option=ai-note:Y,mypage.coupon.view:N   (권장 — 값을 생략하면 Y)
     *   ?option={"ai-note":"Y"}                  (중괄호/따옴표는 URL 인코딩 필요)
     */
    applyQueryOption() {
      if (!import.meta.client) return

      const raw = new URLSearchParams(window.location.search).get('option')
      if (!raw) return

      try {
        const queryOption = this.parseQueryOption(raw.trim())
        if (Object.keys(queryOption).length > 0) {
          this.option = { ...this.option, ...queryOption }
          console.log('옵션 덮어쓰기:', queryOption)
        }
      } catch (error) {
        console.warn('옵션 쿼리 파싱 오류 (무시):', error.message)
      }
    },

    parseQueryOption(raw) {
      if (raw.startsWith('{')) {
        const parsed = JSON.parse(raw)
        return parsed && typeof parsed === 'object' ? parsed : {}
      }

      return Object.fromEntries(
        raw
          .split(',')
          .map((pair) => {
            const [key, value = 'Y'] = pair.split(':')
            return [key.trim(), value.trim()]
          })
          .filter(([key]) => key),
      )
    },
  },
})
